import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

import { STOCK_RETURN_TRUNCATED_NOTICE_CLASS } from './stockReturnTruncatedNoticeClass';
import { formatDisplayQty } from '#/utils/formatNumber';

/** 每行「庫存編號 {no} ．數量 {qty}」（全形．）；多行以 \\n 分隔。 */
function formatOperatingTip(lines = []) {
  return lines
    .map((line) => (
      `庫存編號 ${line.stockNo || '—'} ．數量 ${formatDisplayQty(Number(line.qty) || 0)}`
    ))
    .join('\n');
}

function OperatingQtyCell({ qty, tipLines = [] }) {
  const label = formatDisplayQty(qty);
  const tip = tipLines.length > 0 ? formatOperatingTip(tipLines) : null;
  const qtyClassName = 'w-fit font-medium text-primary';

  if (!tip) {
    return <p className={qtyClassName}>{label}</p>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className={cn(qtyClassName, 'cursor-help')}>{label}</p>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs whitespace-pre-line">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

OperatingQtyCell.propTypes = {
  qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tipLines: PropTypes.arrayOf(PropTypes.shape({
    stockNo: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
};

function materialRowClassName({ hasDraft, focused }) {
  if (focused) {
    return 'bg-primary-200/65 hover:bg-primary-200/65';
  }
  if (hasDraft) {
    return 'bg-primary-50/60 hover:bg-primary-50';
  }
  return 'hover:bg-slate-50';
}

const StockReturnMaterialList = ({
  materials = [],
  operatingByMtrId = {},
  draftLinesByMtrId = {},
  focusedMtrId = null,
  focusToken = 0,
  onOpenPicker,
  onFocusMaterial,
  emptyHint,
  keyword = '',
  onKeywordChange,
  keywordDisabled = false,
  truncatedHint = null,
}) => {
  const rowRefs = useRef({});
  const showSearch = typeof onKeywordChange === 'function';

  useEffect(() => {
    if (!focusedMtrId) {
      return;
    }
    const node = rowRefs.current[focusedMtrId];
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedMtrId, focusToken, materials]);

  return (
    <div className="space-y-3">
      {showSearch ? (
        <div className="sticky top-0 left-0 bg-panel z-10">
          <div className="flex min-w-0 items-center gap-1.5">
            <TableSearchInput
              useKeyword
              keyword={keyword}
              onKeywordChange={onKeywordChange}
              placeholder="搜尋庫存編號、料號、名稱"
              className="min-w-0 w-full max-w-md flex-1 text-foreground"
              disabled={keywordDisabled}
            />
          </div>
          {truncatedHint ? (
            <p className={STOCK_RETURN_TRUNCATED_NOTICE_CLASS}>
              {truncatedHint}
            </p>
          ) : null}
        </div>
      ) : null}


      {materials.length === 0 ? (
        <p className="form-section__empty-state">
          {emptyHint || '請先選擇來源倉以載入可用庫存。'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <ul className="m-0 min-w-[36rem] list-none divide-y divide-gray-200 border border-gray-200 rounded-md p-0">
            {materials.map((row, index) => {
              const operatingQty = Number(operatingByMtrId[row.mtrId]) || 0;
              const tipLines = draftLinesByMtrId[row.mtrId] ?? [];
              const hasDraft = tipLines.length > 0;
              const focused = focusedMtrId === row.mtrId;

              return (
                <li
                  key={`${row.mtrId || 'mtr'}-${index}`}
                  ref={(node) => {
                    if (node) {
                      rowRefs.current[row.mtrId] = node;
                    } else {
                      delete rowRefs.current[row.mtrId];
                    }
                  }}
                  className={cn(
                    'grid cursor-pointer items-center gap-2 px-3 py-3 transition-colors',
                    'grid-cols-[minmax(10rem,1.4fr)_minmax(5rem,8rem)_minmax(5rem,8rem)_auto]',
                    materialRowClassName({ hasDraft, focused }),
                  )}
                  onClick={() => onFocusMaterial?.(row.mtrId)}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {row.mtrName || '（未命名）'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {row.mtrNo || row.mtrId}
                    </p>
                  </div>
                  <div className="text-sm">
                    <p className="text-xs text-gray-500">可用量</p>
                    <p className="font-medium">{formatDisplayQty(row.onHandQty)}</p>
                  </div>
                  <div className="text-sm">
                    <p className="text-xs text-gray-500">操作中</p>
                    <OperatingQtyCell qty={operatingQty} tipLines={tipLines} />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenPicker(row);
                      }}
                    >
                      選庫
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

StockReturnMaterialList.propTypes = {
  materials: PropTypes.arrayOf(PropTypes.shape({
    mtrId: PropTypes.string,
    mtrNo: PropTypes.string,
    mtrName: PropTypes.string,
    onHandQty: PropTypes.number,
    sources: PropTypes.array,
  })),
  operatingByMtrId: PropTypes.objectOf(PropTypes.number),
  draftLinesByMtrId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    stockNo: PropTypes.string,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }))),
  focusedMtrId: PropTypes.string,
  focusToken: PropTypes.number,
  onOpenPicker: PropTypes.func.isRequired,
  onFocusMaterial: PropTypes.func,
  emptyHint: PropTypes.string,
  keyword: PropTypes.string,
  onKeywordChange: PropTypes.func,
  keywordDisabled: PropTypes.bool,
  truncatedHint: PropTypes.string,
};

export default StockReturnMaterialList;
